import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { SCAN_API, estimate, type ScanService } from '@chess/api-scan';
import { useScanProgress } from '@chess/api-scan/react';
import { SYNC_API, type SyncService } from '@chess/api-sync';
import { useSyncState } from '@chess/api-sync/react';
import { useStoreQuery } from '@chess/api-model/react';
import { reviewPath } from '@chess/api-games';
import { SyncScreen } from './SyncScreen.tsx';
import { syncView } from './steps.ts';
import './sync.css';

// The sync screen, `/sync`: where the daily sync runs before the app shows.
// Its gate (the frame's `shell.gate` slot) sends any screen on the rail here
// while a sync is due (`sync.due()`: an account, and no sync completed or
// skipped today); the landing page, this page and a review opened by its
// address are never gated. The screen runs the sync, shows its two steps
// (the import, then the engine's scan of what is new), and goes back to the
// address asked for by itself once everything is over; the user may go on
// earlier while the scan continues (then the day counts as synced, `skip`),
// retry a failed import, or open the latest game as soon as it is scanned.
// The landing page comes here with `force` in the router state: a save there
// always syncs, the same account or another, synced today or not. Entering waits for the latest game to be read (the home shows it, and the
// queue scans newest first, so it is the first one): before that there is no
// way out but a failed import. Once a day, so after the first time it is a
// few seconds.

/** How long the import's end and an idle queue must hold before the screen trusts that nothing is left to scan: the scan pass is queued a tick after the import. */
const SETTLE_MS = 1500;

/** Routes the gate leaves alone. */
function ungated(pathname: string): boolean {
  return pathname === '/welcome' || pathname === '/sync' || pathname.startsWith('/review/');
}

export function setup(app: PiletApi) {
  const brand = app.tryGetApi('brand');

  const useService = <T,>(name: string): T | undefined => {
    const [service, setService] = React.useState<T | undefined>(() => app.tryGetApi<T>(name));
    React.useEffect(() => {
      if (!service) void app.getApi<T>(name).then(setService);
    }, [service]);
    return service;
  };

  const Page: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Where the gate sent the user from, the home otherwise; and whether to
    // sync even on a synced day (the landing page's way in, always a sync).
    const { from = '/', force = false } = (location.state as { from?: string; force?: boolean } | null) ?? {};
    const sync = useService<SyncService>(SYNC_API);
    const scan = useService<ScanService>(SCAN_API);
    const state = useSyncState(sync);
    const progress = useScanProgress(scan);
    // The newest game held, and whether it is scanned: the way in while the rest scan.
    const latest = useStoreQuery(app.getApi, (_store, s) => s.games[0], []);
    // Whether this account had ever synced when the screen opened: the
    // words differ. Read once, since the sync marks the day as it ends.
    const [first, setFirst] = React.useState<boolean | undefined>(undefined);

    // Runs the sync once the service is found, or leaves when none is due
    // (the address typed by hand on a synced day) unless forced.
    React.useEffect(() => {
      if (!sync) return;
      let live = true;
      void sync.due().then((due) => {
        if (!live) return;
        if (!due && !force) {
          navigate(from, { replace: true });
          return;
        }
        setFirst(sync.state().first);
        void sync.run();
      });
      return () => {
        live = false;
      };
    }, [sync]);

    const left = progress ? Math.max(0, progress.total - progress.done) : 0;
    const latestReady = latest?.facts?.scanned ? latest : undefined;
    const view = syncView({
      sync: state ?? { phase: 'idle', first: true },
      scan: progress,
      estimate: scan && progress?.running ? estimate(left, scan.secondsPerGame()) : undefined,
      latest: !latest ? 'none' : latestReady ? 'scanned' : 'pending',
    });

    // Everything over: back to where the user was going, after the queue
    // has had its tick to start (or to stay idle for good).
    React.useEffect(() => {
      if (!view.complete) return;
      const t = setTimeout(() => navigate(from, { replace: true }), SETTLE_MS);
      return () => clearTimeout(t);
    }, [view.complete, from]);

    const leave = (to: string) => {
      sync?.skip();
      navigate(to, { replace: true });
    };
    return (
      <SyncScreen
        brandName={brand.name}
        first={first ?? false}
        view={view}
        latest={
          latestReady && {
            label: `${latestReady.summary.white.name} vs ${latestReady.summary.black.name}`,
            onOpen: () => leave(reviewPath(latestReady.summary)),
          }
        }
        onContinue={() => leave(from)}
        onRetry={() => void sync?.run()}
      />
    );
  };

  app.registerPage('/sync', Page);
  // The whole viewport: no rail, no bottom bar, like the landing page.
  app.registerExtension('shell.chromeless', () => null, { path: '/sync' });

  // The gate: on every screen on the rail, while a sync is due, go to
  // `/sync` first, remembering where to come back to. Draws nothing. Without
  // an account `due` is false and the landing page's own gate acts.
  const Gate: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    React.useEffect(() => {
      const path = location.pathname;
      if (ungated(path)) return;
      let live = true;
      void app
        .getApi<SyncService>(SYNC_API)
        .then((s) => s.due())
        .then((due) => {
          if (live && due) navigate('/sync', { replace: true, state: { from: `${path}${location.search}` } });
        })
        .catch(() => undefined);
      return () => {
        live = false;
      };
    }, [location.pathname]);
    return null;
  };
  app.registerExtension('shell.gate', Gate);
}
